import { useDesign } from "./store";
import { useLibrary } from "./library";
import { useProducts } from "./products";

/**
 * Prefixes the app uses for all of its `localStorage` entries. Anything
 * starting with one of these is owned by Room Canvas and safe to wipe
 * on a full logout. Tracked centrally so adding a new persisted key is
 * a one-line change here.
 *
 *   - "room-canvas:" — Zustand persist namespaces (design/library/products),
 *                      onboarding-seen flag, per-user library-tip flag.
 *   - "roomcanvas:"  — feature-flag overrides, premium dev override.
 */
const APP_STORAGE_PREFIXES: readonly string[] = [
  "room-canvas:",
  "roomcanvas:",
];

/**
 * Wipe every trace of the signed-in user from this device:
 *   1. Reset the in-memory Zustand stores back to their defaults so the
 *      UI snaps to a blank canvas / empty library / empty product list.
 *   2. Remove all app-owned `localStorage` keys so a reload doesn't
 *      rehydrate the previous user's data.
 *
 * Intended to be called as the *final* step of {@link signOut} —
 * **after** `firebase/auth signOut` has resolved, which is what causes
 * `librarySync` / `productsSync` / `sharedRoomsRegistry` to tear down
 * their store subscribers. Doing it in this order matters: if the
 * subscribers were still attached when we cleared the local stores,
 * they'd interpret the local wipe as a deletion and echo it up to
 * Firestore, blowing away the user's *cloud* data too.
 *
 * Safe to call when Firebase isn't configured (no-op outside the
 * store/localStorage cleanup).
 */
export function clearLocalAppData(): void {
  // 1. Reset in-memory stores. The persist middleware writes the
  //    cleared state back to localStorage immediately; step 2 below
  //    will then remove those keys outright.
  useDesign.getState().resetAll();
  useLibrary.getState().clearAll();
  useProducts.getState().clearAll();

  // 2. Strip every app-owned localStorage entry. We iterate the keys
  //    rather than `localStorage.clear()` so we don't trample storage
  //    other code on the page might own (extensions, future host
  //    integrations, etc.).
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (!key) continue;
      if (APP_STORAGE_PREFIXES.some((p) => key.startsWith(p))) {
        toRemove.push(key);
      }
    }
    for (const key of toRemove) {
      window.localStorage.removeItem(key);
    }
  } catch {
    // Storage access can fail in locked-down browser contexts (private
    // mode quotas, disabled cookies). The in-memory reset above is the
    // primary user-visible effect — losing the localStorage cleanup is
    // acceptable as a fallback.
  }
}
